import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2 } from 'lucide-react'
import { listMyApps, browseApps, type App } from '../../lib/api'
import { LoadingRow } from '../ui/AsyncStates'

interface SearchResult {
  app: App
  section: 'my-apps' | 'browse'
}

export default function AppSearchBar() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [myApps, setMyApps] = useState<App[]>([])
  const [browseAppsList, setBrowseAppsList] = useState<App[]>([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Load data on first focus
  const loadData = useCallback(async () => {
    if (dataLoaded) return
    setLoading(true)
    try {
      const [my, browse] = await Promise.all([listMyApps(), browseApps()])
      setMyApps(my)
      setBrowseAppsList(browse)
      setDataLoaded(true)
    } catch (err) {
      console.error('Failed to load search data:', err)
    } finally {
      setLoading(false)
    }
  }, [dataLoaded])

  // Filter results when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    const q = query.toLowerCase()
    const myMatches = myApps
      .filter(a => matchApp(a, q))
      .map(a => ({ app: a, section: 'my-apps' as const }))

    // Exclude apps already in myApps from browse results
    const myAppNames = new Set(myApps.map(a => a.name))
    const browseMatches = browseAppsList
      .filter(a => !myAppNames.has(a.name) && matchApp(a, q))
      .map(a => ({ app: a, section: 'browse' as const }))

    setResults([...myMatches.slice(0, 5), ...browseMatches.slice(0, 5)])
  }, [query, myApps, browseAppsList])

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Cmd/Ctrl+K shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
        loadData()
      }
      if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [loadData])

  function handleSelect(app: App) {
    setOpen(false)
    setQuery('')
    navigate(`/apps/${app.name}`)
  }

  const myResults = results.filter(r => r.section === 'my-apps')
  const browseResults = results.filter(r => r.section === 'browse')
  const showDropdown = open && (query.trim().length > 0 || loading)

  return (
    <div ref={containerRef} className="relative hidden md:block">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-transparent text-muted-foreground hover:border-border/80 transition-colors w-56">
        <Search className="w-4 h-4 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { setOpen(true); loadData() }}
          placeholder="Search apps..."
          className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
        />
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card shadow-lg z-50 max-h-80 overflow-y-auto">
          {loading && results.length === 0 ? (
            <LoadingRow label="Searching…" className="p-3" />
          ) : results.length === 0 && query.trim() ? (
            <div className="p-3 text-sm text-muted-foreground text-center">No apps found</div>
          ) : (
            <>
              {myResults.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider bg-secondary/50">
                    My Apps
                  </div>
                  {myResults.map(({ app }) => (
                    <ResultItem key={`my-${app.name}`} app={app} onSelect={handleSelect} />
                  ))}
                </div>
              )}
              {browseResults.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider bg-secondary/50">
                    Browse
                  </div>
                  {browseResults.map(({ app }) => (
                    <ResultItem key={`browse-${app.name}`} app={app} onSelect={handleSelect} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ResultItem({ app, onSelect }: { app: App; onSelect: (app: App) => void }) {
  const statusColors: Record<string, string> = {
    running: 'bg-green-500',
    stopped: 'bg-gray-400',
    starting: 'bg-amber-500',
    error: 'bg-red-500',
  }

  return (
    <button
      onClick={() => onSelect(app)}
      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-secondary/50 transition-colors text-left"
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${statusColors[app.status] || 'bg-gray-400'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{app.display_name || app.name}</p>
        {app.description && (
          <p className="text-xs text-muted-foreground truncate">{app.description}</p>
        )}
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0">
        {app.owner_name || app.owner_email?.split('@')[0] || ''}
      </span>
    </button>
  )
}

function matchApp(app: App, query: string): boolean {
  const name = (app.display_name || app.name || '').toLowerCase()
  const desc = (app.description || '').toLowerCase()
  const owner = (app.owner_name || app.owner_email || '').toLowerCase()
  return name.includes(query) || desc.includes(query) || owner.includes(query)
}
